/* eslint-disable max-lines */

import pageSizes from './pageSizes';
import getMagneticDeclination from '../../../../utils/getMagneticDeclination';
import gaiaLogo from '../../../../../../gaia/static/img/logos_icons/gaia-gps-logo-white-stroke.png';

export const pageMargin = 0.25; // measured in inches
const padding = 0.1;

interface GeneratePDFMapProps {
    orientation: string;
    size: MapPrintSidebar['size'];
    map: mapboxgl.Map | undefined;
    attribution: string[][] | [];
    showCompass: boolean;
    scalebar: {width: number; label: string};
    jspdfPromise: Promise<{default: any; prototype: any}>;
}

export default function generatePDFMap(props: GeneratePDFMapProps) {
    return new Promise(resolve => {
        const {map} = props;
        if (!map) return resolve();

        map.once('render', () => {
            resolve(drawPDF({...props, canvas: map.getCanvas()}));
        });

        map.setBearing(map.getBearing());
    });
}

function drawPDF({
    orientation,
    size,
    attribution,
    showCompass,
    map,
    canvas,
    scalebar,
    jspdfPromise,
}: GeneratePDFMapProps & {canvas: any}) {
    return new Promise(async resolve => {
        if (!map) return resolve();
        const JSPDF = (await jspdfPromise).default;

        const mapWidth = (
            (orientation === 'portrait') ?
                pageSizes[size].width :
                pageSizes[size].height
        ) - (pageMargin * 2);
        const mapHeight = (
            (orientation === 'portrait') ?
                pageSizes[size].height :
                pageSizes[size].width
        ) - (pageMargin * 2);

        let pdf = new JSPDF({
            orientation: orientation,
            unit: 'in',
            format: size,
            compress: true,
        });

        // Neat line
        pdf.setDrawColor('#000000');
        pdf.setLineWidth(0.05);
        pdf.rect(pageMargin, pageMargin, mapWidth, mapHeight, 'S');

        pdf.addImage(canvas, 'PNG', pageMargin, pageMargin, mapWidth, mapHeight, null, 'NONE');

        pdf.addImage(
            gaiaLogo,
            'PNG',
            pageMargin + padding,
            pageMargin + padding,
            1.25,
            0.3625,
            null,
            'NONE'
        );

        if (showCompass) {
            pdf = drawCompass({pdf, map, mapWidth});
        }

        // Attribution box
        const attributionHeight = 0.35;
        const attrX = pageMargin;
        const attrY = mapHeight - attributionHeight + pageMargin;
        pdf.setFillColor('#ffffff');
        pdf.rect(attrX, attrY, mapWidth, attributionHeight, 'F');

        // Attribution text
        pdf.setFillColor('#000000');
        pdf.setFontSize(6);
        let currentX = attrX + padding;
        attribution.forEach(attr => {
            pdf.setFont('helvetica', 'bold');
            pdf.text(
                attr[0] + ': ',
                currentX,
                attrY + padding,
                {charSpace: 0.005, fontSize: 6, font: 'helvetica'})
            ;
            currentX += (
                pdf.getStringUnitWidth(attr[0] + ': ', {charSpace: 0.005, fontSize: 6}) * 7
            ) / 72;

            pdf.setFont('helvetica', 'normal');
            pdf.text(
                attr[1],
                currentX,
                attrY + padding,
                {charSpace: 0, fontSize: 6, font: 'helvetica'}
            );
            currentX += (
                pdf.getStringUnitWidth(attr[1], {charSpace: 0, fontSize: 6}) * 7
            ) / 72;
        });

        const glCanvasWidth = parseInt(
            (document.querySelector('.mapboxgl-canvas') as any)
                .style.width.replace('px', '')
        );

        const scaleRatio = glCanvasWidth / scalebar.width;
        const printScaleWidth =  mapWidth / scaleRatio;

        // Scalebar
        const scaleBarX1 = mapWidth - printScaleWidth;
        const scaleBarX2 = scaleBarX1 + printScaleWidth;
        pdf.setDrawColor('#000000');
        pdf.setLineWidth(0.01);

        pdf.line(
            scaleBarX1,
            mapHeight + pageMargin - padding - 0.1,
            scaleBarX2,
            mapHeight + pageMargin - padding - 0.1,
        );
        pdf.line(
            scaleBarX1,
            mapHeight + pageMargin - padding - 0.1,
            scaleBarX1,
            mapHeight + pageMargin - padding - 0.15
        );
        pdf.line(
            scaleBarX2,
            mapHeight + pageMargin - padding - 0.1,
            scaleBarX2,
            mapHeight + pageMargin - padding - 0.15,
        );

        pdf.setFontSize(5);
        pdf.text(
            scalebar.label,
            ((scaleBarX1 + scaleBarX2) / 2) - ((scalebar.label.length * 0.04) / 2),
            mapHeight + pageMargin - padding - 0.125,
            {charSpace: 0},
        );

        const proj = 'Web Mercator | EPSG: 3857';
        pdf.text(
            proj,
            ((scaleBarX1 + scaleBarX2) / 2) - ((proj.length * 0.035) / 2),
            mapHeight + pageMargin - padding - 0.005,
            {charSpace: 0},
        );

        pdf.save(`GaiaGPS-${new Date().toISOString().split('.')[0]}.pdf`);
        resolve();
    });
}

interface DrawCompassProps {
    pdf: any;
    map: mapboxgl.Map;
    mapWidth: number;
}
function drawCompass({pdf, map, mapWidth}: DrawCompassProps) {
    const declination = getMagneticDeclination(map.getCenter());

    pdf.setDrawColor('#ffffff');
    pdf.setLineWidth(0.05);
    pdf.setLineCap('bevel');

    const theta = declination * (Math.PI / 180) * -1;

    const x = mapWidth - pageMargin;
    const y = pageMargin + padding + 0.2;

    const cx = mapWidth - pageMargin;
    const cy = pageMargin + padding + 0.5;

    const newX = ((x - cx) * Math.cos(theta) + (y - cy) * Math.sin(theta)) + cx;
    const newY = (-(x - cx) * Math.sin(theta) + (y - cy) * Math.cos(theta)) + cy;

    const bottomArrowTranslate = 0.04;

    const magneticLineDistance = Math.sqrt(
        Math.pow(x - newX, 2) + Math.pow(pageMargin + padding + 0.5 - newY, 2)
    );
    const t = 0.05 / magneticLineDistance;

    const unrotatedX = (1 - t) * newX + (t * x);
    const unrotatedY = (1 - t) * newY + (t * (pageMargin + padding + 0.5));

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.setFillColor(255, 255, 255);
    pdf.setLineWidth(0.03);
    pdf.setLineCap('rounded');

    pdf.text('N', cx - 0.04, cy + 0.12, {
        renderingMode: 'stroke'
    });
    pdf.text('N', cx - 0.04, cy + 0.12, {
        renderingMode: 'fill'
    });

    pdf.setFontSize(6);
    pdf.setLineWidth(0.02);
    pdf.text([`Declination: ${declination}°`], cx + 0.03, cy + 0.24, {
        align: 'center',
        renderingMode: 'stroke'
    });
    pdf.text([`Declination: ${declination}°`], cx + 0.03, cy + 0.24, {
        align: 'center',
        renderingMode: 'fill'
    });

    // Draw white casing
    pdf.setLineWidth(0.05);
    pdf.setLineCap('bevel');

    // North arrow line
    pdf.line(x, pageMargin + padding + bottomArrowTranslate, cx, cy);

    // Magnetic declination line
    pdf.line(x, pageMargin + padding + 0.5, newX, newY);

    // Magnetic declination arrow
    pdf.line(
        newX,
        newY,
        (
            ((unrotatedX - newX) * Math.cos(45 * (Math.PI / 180)) +
            (unrotatedY - newY) * Math.sin(45 * (Math.PI / 180))) +
            newX
        ),
        (
            (-(unrotatedX - newX) * Math.sin(45 * (Math.PI / 180)) +
            (unrotatedY - newY) * Math.cos(45 * (Math.PI / 180))) +
            newY
        ),
    );
    pdf.line(
        newX,
        newY,
        (
            ((unrotatedX - newX) * Math.cos(-45 * (Math.PI / 180)) +
            (unrotatedY - newY) * Math.sin(-45 * (Math.PI / 180))) +
            newX
        ),
        (
            (-(unrotatedX - newX) * Math.sin(-45 * (Math.PI / 180)) +
            (unrotatedY - newY) * Math.cos(-45 * (Math.PI / 180))) +
            newY
        ),
    );

    // top left arrow
    pdf.line(
        x,
        pageMargin + padding,
        x - 0.07,
        pageMargin + padding + 0.07,
    );
    // top right arrow
    pdf.line(
        x,
        pageMargin + padding,
        x + 0.07,
        pageMargin + padding + 0.07,
    );
    // bottom left arrow
    pdf.line(
        x,
        pageMargin + padding + bottomArrowTranslate,
        x - 0.06,
        pageMargin + padding + 0.07 + bottomArrowTranslate,
    );
    // bottom right arrow
    pdf.line(
        x,
        pageMargin + padding + bottomArrowTranslate,
        x + 0.06,
        pageMargin + padding + 0.07 + bottomArrowTranslate,
    );

    // Draw black stroke
    pdf.setDrawColor('#000000');
    pdf.setLineWidth(0.015);
    pdf.setLineCap('bevel');

    // North arrow line
    pdf.line(x, pageMargin + padding + bottomArrowTranslate, cx, cy);

    // Magnetic declination line
    pdf.line(x, pageMargin + padding + 0.5, newX, newY);

    // Magnetic declination arrow
    pdf.line(
        newX,
        newY,
        (
            ((unrotatedX - newX) * Math.cos(45 * (Math.PI / 180)) +
            (unrotatedY - newY) * Math.sin(45 * (Math.PI / 180))) +
            newX
        ),
        (
            (-(unrotatedX - newX) * Math.sin(45 * (Math.PI / 180)) +
            (unrotatedY - newY) * Math.cos(45 * (Math.PI / 180))) +
            newY
        ),
    );
    pdf.line(
        newX,
        newY,
        (
            ((unrotatedX - newX) * Math.cos(-45 * (Math.PI / 180)) +
            (unrotatedY - newY) * Math.sin(-45 * (Math.PI / 180))) +
            newX
        ),
        (
            (-(unrotatedX - newX) * Math.sin(-45 * (Math.PI / 180)) +
            (unrotatedY - newY) * Math.cos(-45 * (Math.PI / 180))) +
            newY
        ),
    );

    // top left arrow
    pdf.line(
        x,
        pageMargin + padding,
        x - 0.07,
        pageMargin + padding + 0.07,
    );
    // top right arrow
    pdf.line(
        x,
        pageMargin + padding,
        x + 0.07,
        pageMargin + padding + 0.07,
    );
    // bottom left arrow
    pdf.line(
        x,
        pageMargin + padding + bottomArrowTranslate,
        x - 0.06,
        pageMargin + padding + 0.07 + bottomArrowTranslate,
    );
    // bottom right arrow
    pdf.line(
        x,
        pageMargin + padding + bottomArrowTranslate,
        x + 0.06,
        pageMargin + padding + 0.07 + bottomArrowTranslate,
    );

    return pdf;
}
